import React from 'react';
import {
  styled,
  TableRow,
  Tooltip,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Link,
  Collapse,
  Table,
  TableHead,
  TableCell,
  Box,
  tableCellClasses,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ListIcon from '@mui/icons-material/List';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockIcon from '@mui/icons-material/Block';
import { LabeledInput } from '../labeled-input';
import { ItemImage } from '../item-image';
import { ErpLink } from '../erp-link';
import { camelCaseReplace } from '../../../helpers/stringHelpers';
import { updateRowValue } from './tableHelpers';

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
    fontFamily: 'Arial',
  },
}));

const formatIcon = (icon) => {
  switch (icon) {
    case 'edit':
      return <EditIcon />;
    case 'block':
      return <BlockIcon />;
    case 'delete':
      return <DeleteOutlineIcon />;
    case 'save':
      return <SaveIcon />;
    case 'add':
      return <AddIcon />;
    case 'list':
      return <ListIcon />;
    case 'location':
      return <ApartmentIcon fontSize='large' />;
    default:
      return null;
  }
};

// eslint-disable-next-line react/display-name
export const RowValue = React.memo(
  ({
    row,
    rowKey: key,
    setRow = () => {},
    accordionIndex = null,
    handleSetParentState = () => {},
    getRowHeight = () => {},
    updateRowHeight = () => {},
    openNestedDataPopUp = () => {},
    buttonColor,
    buttonText,
    rowButtonHeight = '35px',
  }) => {
    const isExpandedIndex = row[key]?.open;
    const setRowKeys = row[key]?.setRowKey || [];
    const setSubRowKeys = row[key]?.setSubRowKey || [];
    const accordionKey = row[key]?.accordionKey;
    const saveKey = row[key]?.saveKey;
    const isObject = row[key] && typeof row[key] === 'object';

    return isObject && row[key]?.button ? (
      <Tooltip title={row[key]?.hoverText || ''}>
        <Button
          variant={row[key]?.variant || 'outlined'}
          {...(!row[key]?.width
            ? { size: 'small' }
            : { sx: { width: row[key]?.width, height: '30px' } })}
          disabled={row[key]?.disabled}
          {...(row[key]?.endIcon && {
            endIcon: formatIcon(row[key]?.endIcon),
          })}
          {...(row[key]?.startIcon && {
            startIcon: formatIcon(row[key]?.startIcon),
          })}
          sx={
            row[key]?.fontSize
              ? { fontSize: row[key]?.fontSize }
              : { fontSize: '10px' }
          }
          color={row[key]?.color || 'primary'}
          onClick={() => {
            handleSetParentState(row, row[key]?.value, key);
          }}
        >
          {row[key]?.buttonTitle}
        </Button>
      </Tooltip>
    ) : isObject && row[key]?.iconButton ? (
      <Tooltip title={row[key]?.hoverText || ''}>
        <IconButton
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            handleSetParentState(row, row[key]?.value, key);
          }}
          disabled={row[key]?.disabled}
          size='small'
          color={row[key]?.color || 'warning'}
        >
          {formatIcon(row[key]?.icon)}
        </IconButton>
      </Tooltip>
    ) : isObject && row[key]?.toolTip ? (
      <Tooltip title={row[key]?.toolTipTitle || 'View Details'}>
        <Button
          sx={{ cursor: 'pointer', fontSize: '10px', height: '35px' }}
          disabled={row[key]?.disabled}
          size='small'
          color='warning'
          variant='outlined'
          onClick={() => openNestedDataPopUp(row[key]?.rows)}
        >
          {row?.[key]?.toolTipTitle}
        </Button>
      </Tooltip>
    ) : isObject && row[key]?.accordion ? (
      <Tooltip title={row[key]?.hoverText || ''}>
        <Button
          sx={{
            cursor: 'pointer',
            fontSize: isExpandedIndex ? '10px' : '12px',
            color: isExpandedIndex
              ? 'white'
              : `${buttonText || buttonColor || 'warning'}`,
            height: rowButtonHeight,
            width: row[key].accordion.width || '100%',
            border: 'transparent',
            m: 0.25,
          }}
          disabled={row[key]?.disabled}
          size='small'
          color={buttonColor || 'warning'}
          variant={isExpandedIndex ? 'contained' : 'outlined'}
          endIcon={isExpandedIndex ? <ExpandLess /> : <ExpandMoreIcon />}
          onClick={() => {
            let newRowHeight;
            if (!isExpandedIndex) {
              const numRows = Array.isArray(row[key]?.rows)
                ? row[key].rows.length
                : 0;
              newRowHeight =
                getRowHeight(row) + (numRows > 0 ? numRows * 52 : 0);
            } else {
              newRowHeight = undefined;
            }
            updateRowHeight(row.id, newRowHeight);
            setRow(
              Object.fromEntries(
                Object.entries(row).map(([k, v]) => [
                  k,
                  k === key
                    ? { ...v, open: !v?.open }
                    : v?.accordion && v?.open
                    ? { ...v, open: false }
                    : v,
                ])
              ),
              row.id
            );
          }}
        >
          {row?.[key]?.accordionTitle}
        </Button>
      </Tooltip>
    ) : row[key]?.dropDownSelect ? (
      <FormControl variant='standard' size='small' fullWidth>
        <InputLabel id={`select-${key}`}>{camelCaseReplace(key)}</InputLabel>
        <Select
          size='small'
          sx={{
            height: '35px',
            width: '100%',
            backgroundColor: 'white',
            color: 'black',
            borderRadius: '4px',
          }}
          disabled={row[key]?.disabled}
          value={row[key]?.updatedValue || row[key]?.value || ''}
          onChange={(event) => {
            const updatedValue = event.target.value;
            if (!accordionIndex) {
              setRow(
                updateRowValue(
                  row,
                  key,
                  updatedValue,
                  setRowKeys,
                  setSubRowKeys,
                  accordionKey,
                  saveKey
                ),
                row.id
              );
            }
            if (accordionIndex != null && +accordionIndex >= 0) {
              setRow(key, accordionIndex, updatedValue, saveKey);
            }
          }}
        >
          <MenuItem value={null}>Clear Selection</MenuItem>
          {row[key]?.options?.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : row[key]?.checkBox ? (
      <LabeledInput
        type='checkbox'
        disabled={row[key]?.disabled}
        value={row[key]?.updatedValue || row[key]?.value || ''}
        title={row[key]?.title || camelCaseReplace(key)}
        onChange={(e) => {
          const updatedValue = e.target.checked ? 'Y' : 'N';
          if (!accordionIndex) {
            setRow(
              updateRowValue(
                row,
                key,
                updatedValue,
                setRowKeys,
                setSubRowKeys,
                accordionKey,
                saveKey
              ),
              row.id
            );
          }
          if (accordionIndex != null && +accordionIndex >= 0) {
            setRow(key, accordionIndex, updatedValue, saveKey);
          }
        }}
      />
    ) : row[key]?.input ? (
      <Stack direction='row' spacing={1} alignItems='center'>
        <LabeledInput
          type={row[key]?.type || 'text'}
          {...(['date', 'mobileDate'].includes(row[key]?.type)
            ? { useDateFns: true }
            : {})}
          {...(row[key]?.disablePast && { disablePast: true })}
          {...(row[key]?.multiline && {
            multiline: true,
            multilineRows: row[key]?.multilineRows || 3,
          })}
          variant={row[key]?.variant || 'standard'}
          title={row[key]?.hoverText || ''}
          value={row[key]?.value || ''}
          disabled={row[key]?.disabled}
          onChange={(e) => {
            // Handle both event and direct value changes
            const updatedValue = e?.target?.value ?? e ?? '';

            if (!accordionIndex) {
              setRow(
                updateRowValue(
                  row,
                  key,
                  updatedValue,
                  setRowKeys,
                  setSubRowKeys,
                  accordionKey,
                  saveKey
                ),
                row.id
              );
            }
            if (accordionIndex != null && +accordionIndex >= 0) {
              setRow(key, accordionIndex, updatedValue, saveKey);
            }
          }}
          label={camelCaseReplace(key)}
          size={row[key]?.size || 'small'}
          sx={{ width: '100%' }}
          textSx={{ '& .MuiInputBase-input ': { fontSize: '12px' } }}
        />
      </Stack>
    ) : row[key]?.stack ? (
      <Stack spacing={1} direction={row[key]?.direction || 'column'}>
        {row[key]?.rows?.map((item, index) => (
          <Typography
            key={index}
            variant='caption'
            sx={{ fontSize: '12px', m: 'auto' }}
          >
            {item}
          </Typography>
        ))}
      </Stack>
    ) : row[key]?.textWithColor ? (
      <Typography
        variant='caption'
        sx={{
          fontSize: '12px',
          m: 'auto',
          color: row[key]?.color || 'black',
          fontWeight: row[key]?.fontWeight || 'normal',
        }}
      >
        {row[key]?.value || ''}
      </Typography>
    ) : row[key]?.itemImage ? (
      <ItemImage itemId={row[key]?.itemId || null} />
    ) : isObject && row[key]?.erpLink ? (
      <ErpLink
        query={row[key].query}
        text={row[key]?.linkTitle || 'View Link'}
        title={row[key]?.hoverText || 'View Link'}
        windowName={row[key]?.windowName}
        style={{
          fontSize: row[key]?.fontSize || '13px',
          color: 'cornflowerblue',
          cursor: 'pointer',
          fontWeight: row[key]?.fontWeight || 'bold',
        }}
      />
    ) : isObject && row[key]?.link ? (
      <Stack
        direction='row'
        spacing={0}
        alignItems='center'
        justifyContent={row[key]?.justifyContent || 'space-between'}
      >
        <Link
          href={row[key]?.href}
          title={row[key]?.hoverText || 'View Link'}
          target='_blank'
          style={{
            fontSize: row[key]?.fontSize || '13px',
            color: 'cornflowerblue',
            cursor: 'pointer',
          }}
        >
          {row[key]?.linkTitle || 'View Link'}
        </Link>
        {row[key]?.erpWindowLink?.openWindowIcon ? (
          <IconButton
            color={row[key]?.erpWindowLink?.color || 'primary'}
            disabled={row[key]?.erpWindowLink?.disabled || false}
            onClick={() =>
              handleSetParentState(row || {}, 'input-' + key, '', key)
            }
          >
            <ErpLink
              query={row[key]?.erpWindowLink?.query}
              text={row[key]?.erpWindowLink?.linkTitle || 'View Link'}
              title={row[key]?.erpWindowLink?.hoverText || 'View Link'}
              windowName={row[key]?.erpWindowLink?.windowName}
              openWindowIcon={true}
            />
          </IconButton>
        ) : null}
      </Stack>
    ) : isObject && row[key] ? (
      <Typography sx={{ fontSize: '12px', m: 'auto' }}>
        {row[key]?.value || ''}
      </Typography>
    ) : (
      <Typography sx={{ fontSize: '12px', m: 'auto' }}>{row[key]}</Typography>
    );
  }
);

// eslint-disable-next-line react/display-name
export const MappedRow = React.memo(
  ({
    row,
    index: mappedRowIndex,
    compact,
    buttonColor,
    buttonText,
    rowButtonHeight,
    onRowChange,
    filterFields,
    handleSetParentState,
    getRowHeight,
    updateRowHeight,
    openNestedDataPopUp,
  }) => {
    const accordionKey = Object.keys(row).find(
      (key) => row[key]?.accordion && row[key]?.open
    );
    const accordionValue = row[accordionKey];
    const filteredObjectKeys = Object.keys(row || {}).filter(
      (key) => !filterFields.includes(key) && key
    );

    const ROW_HEIGHT = 90;
    // Function to set value in accordion rows
    const setAccordionValue = (modifiedKey, index, newValue, saveKey) => {
      const updatedAccordion = {
        ...row[accordionKey],
        rows: row[accordionKey]?.rows?.map((childRow, childIndex) =>
          childIndex === index
            ? {
                ...childRow,
                [modifiedKey]: { ...childRow[modifiedKey], value: newValue },
              }
            : childRow
        ),
      };
      const updatedRow = { ...row, [accordionKey]: updatedAccordion };
      if (saveKey) {
        updatedRow[saveKey] = { ...updatedRow[saveKey], disabled: false };
      }
      onRowChange(updatedRow, mappedRowIndex);
    };

    return (
      <>
        {compact ? (
          <TableRow
            hover={true}
            key={`mappedRowIndex_${mappedRowIndex}`}
            sx={{ minHeight: '28px', height: ROW_HEIGHT, padding: 0 }}
          >
            {filteredObjectKeys?.map((key, index) => (
              <TableCell
                size='small'
                key={`mappedRowIndex_${mappedRowIndex}_cell_${index}`}
                sx={{
                  textAlign: 'center',
                  padding: '2px 6px',
                  minWidth: 0,
                  fontSize: '11px',
                  lineHeight: 1.1,
                  verticalAlign: 'middle',
                }}
              >
                <RowValue
                  row={row}
                  rowKey={key}
                  setRow={onRowChange}
                  handleSetParentState={handleSetParentState}
                  getRowHeight={getRowHeight}
                  updateRowHeight={updateRowHeight}
                  openNestedDataPopUp={openNestedDataPopUp}
                  buttonColor={buttonColor}
                  buttonText={buttonText}
                  rowButtonHeight={rowButtonHeight}
                />
              </TableCell>
            ))}
          </TableRow>
        ) : (
          <StyledTableRow
            hover={true}
            key={`mappedRowIndex_${mappedRowIndex}`}
            sx={{ minHeight: '32px', height: ROW_HEIGHT, padding: 0 }}
          >
            {filteredObjectKeys?.map((key, index) => (
              <TableCell
                key={`mappedRowIndex_${mappedRowIndex}_cell_${index}`}
                sx={{
                  textAlign: 'center',
                  padding: '3px 8px',
                  minWidth: 0,
                  fontSize: '12px',
                  lineHeight: 1.15,
                  verticalAlign: 'middle',
                }}
              >
                <RowValue
                  row={row}
                  rowKey={key}
                  setRow={onRowChange}
                  handleSetParentState={handleSetParentState}
                  getRowHeight={getRowHeight}
                  updateRowHeight={updateRowHeight}
                  openNestedDataPopUp={openNestedDataPopUp}
                  buttonColor={buttonColor}
                  buttonText={buttonText}
                  rowButtonHeight={rowButtonHeight}
                />
              </TableCell>
            ))}
          </StyledTableRow>
        )}
        {accordionValue?.open ? (
          <TableRow>
            <TableCell
              colSpan={filteredObjectKeys.length}
              style={{
                paddingBottom: 0,
                paddingTop: 0,
                borderRight: '3px solid rgba(158, 144, 144, 0.4)',
                borderLeft: '3px solid rgba(158, 144, 144, 0.4)',
                borderBottom: '3px solid rgba(158, 144, 144, 0.4)',
                backgroundColor: 'rgba(240, 240, 240, 0.58)',
              }}
            >
              <Collapse in={accordionValue?.open}>
                <Box
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                  }}
                >
                  {accordionValue.rows.length > 0 ? (
                    <Table stickyHeader sx={{ width: '100%' }}>
                      <TableHead>
                        {Object.keys(accordionValue.rows[0]).map(
                          (key, index) => (
                            <TableCell
                              key={`accordionRow_${index}`}
                              sx={{
                                textAlign: 'center',
                                padding: '2px 6px',
                                minWidth: 0,
                                fontSize: '11px',
                                lineHeight: 1.1,
                                verticalAlign: 'middle',
                                [`&.${tableCellClasses.head}`]: {
                                  backgroundColor: 'rgb(169, 171, 173)',
                                },
                              }}
                            >
                              <Stack
                                direction='row'
                                alignItems={'center'}
                                spacing={0}
                              >
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontSize: '12px',
                                    m: 'auto',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    borderBottom: 'none',
                                  }}
                                >
                                  {camelCaseReplace(key)}
                                </Typography>
                              </Stack>
                            </TableCell>
                          )
                        )}
                      </TableHead>
                      {row[accordionKey]?.rows?.map((accordionRow, index1) => (
                        <StyledTableRow key={index1}>
                          {Object.keys(accordionRow).map((key, index) => (
                            <TableCell
                              key={`accordionRow_${index}_cell_${key}`}
                              sx={{
                                textAlign: 'center',
                                padding: '2px 6px',
                                minWidth: 0,
                                fontSize: '11px',
                                lineHeight: 1.1,
                                verticalAlign: 'middle',
                              }}
                            >
                              <Stack
                                direction='row'
                                alignItems={'center'}
                                spacing={0}
                              >
                                <Typography
                                  variant='body1'
                                  sx={{ fontSize: '13px', m: 'auto' }}
                                >
                                  <RowValue
                                    row={accordionRow}
                                    rowKey={key}
                                    setRow={setAccordionValue}
                                    accordionIndex={index1}
                                    handleSetParentState={handleSetParentState}
                                    getRowHeight={getRowHeight}
                                    updateRowHeight={updateRowHeight}
                                    openNestedDataPopUp={openNestedDataPopUp}
                                    buttonColor={buttonColor}
                                    buttonText={buttonText}
                                    rowButtonHeight={rowButtonHeight}
                                  />
                                </Typography>
                              </Stack>
                            </TableCell>
                          ))}
                        </StyledTableRow>
                      ))}
                    </Table>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant='body2' color='text.secondary'>
                        No data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        ) : null}
      </>
    );
  }
);
